import { useComputedValue, useGetMessageHandlers } from "@/composables";
import { GetterTypes, MutationTypes, ActionTypes } from "@/store";
import { AppEnvironment, LayoutTypes } from "@/store/app/state";
import { Attachment, ChatTypes, MessageType } from "@/store/chats/types";
import { convertFilesToAttachments, getFileFromUrl } from "@/utils/attachment";
import { ref, ComponentPublicInstance, reactive, computed, watch } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { ChatInputBarData, ChatInputBarProps } from "./ChatInputBarTypes";
import InputText from "../InputText/InputText.vue";
import {
  convertNonAutomaticMessageToRawMessage,
  createCachedMessage,
  getIsMessageTextType,
} from "@/utils/message";
import { useAudioPlayer } from "../AudioPlayer";
import { useI18n } from "vue-i18n";
import { ISendMessageArgs } from "@/types/chat";
const containerId = "audioPlayer";

export const useGetChatInputBarProperties = (props: ChatInputBarProps) => {
  const { t } = useI18n();
  const { commit, dispatch } = useStore();
  const router = useRouter();
  const messageInput = ref<ComponentPublicInstance<typeof InputText>>();
  const data = reactive<ChatInputBarData>({
    messageText: "",
    attachments: [],
    isEmojiShown: false,
  });

  const chatId = useComputedValue<string>(GetterTypes.GET_SELECTED_CHAT_ID);
  const layoutType = useComputedValue<LayoutTypes>(GetterTypes.GET_LAYOUT_TYPE);
  const sharedMessage = useComputedValue<string | null>(
    GetterTypes.GET_SHARED_MESSAGE,
  );
  const memberIds = computed(
    () => props.members?.map((member) => String(member.id)) ?? [],
  );
  const messageType = computed(() => {
    if (
      recordingData.isRecording ||
      (recordingData.isFinished && recordingData.recordingFile)
    ) {
      return MessageType.Audio;
    }
    return MessageType.Text;
  });

  const isAudioMessageInQueue = ref<boolean>(false);
  const { replyMessage, modifiedMessage, clearInjectedMessage } =
    useGetMessageHandlers();
  const environment = useComputedValue<AppEnvironment>(
    GetterTypes.GET_APP_ENVIRONMENT,
  );

  const textMessagePreviewText = computed(() => {
    const replyMess = replyMessage.value;
    if (replyMess && getIsMessageTextType(replyMess)) {
      return replyMess.displayMessageText;
    }
    return t("chat.chatContainer.messages.audioMessage");
  });

  const getAppliedAttachments = async (attachments: Attachment[]) => {
    if (messageType.value === MessageType.Audio) {
      const files = [recordingData.recordingFile].filter(Boolean) as File[];
      const attachments = await convertFilesToAttachments(files);
      return attachments.map((att) => ({
        ...att,
        duration: recordingData.totalTime,
      }));
    }
    return attachments;
  };

  const {
    stopRecording,
    recordingData,
    startRecording,
    clearRecording,
    audioPlayerProps,
    isRecordingOrFinished,
  } = useAudioPlayer({
    isRecorder: true,
    isMine: true,
    containerId,
  });

  watch(
    sharedMessage,
    () => {
      if (sharedMessage.value) {
        data.messageText = sharedMessage.value;
        commit(MutationTypes.SET_SHARED_MESSAGE, "");
      }
    },
    { immediate: true },
  );
  watch(
    modifiedMessage,
    (val, oldVal) => {
      if (!val || val === oldVal || val.messageText === data.messageText) {
        return;
      }
      data.messageText = val.messageText;
    },
    { immediate: true },
  );

  const messageInputHeight = useComputedValue(
    GetterTypes.GET_LAYOUT_MESSAGE_INPUT_HEIGHT,
  );

  const isInputShown = computed<boolean>(() => {
    if (
      environment.value.isMobileApp &&
      chatId.value === ChatTypes.NEW_CHAT_TYPE &&
      !props.members?.length
    ) {
      return false;
    }
    return true;
  });
  const isMobile = computed<boolean>(
    () => layoutType.value === LayoutTypes.MOBILE,
  );
  const getTrimmedText = (text: string) => text.trim();

  const toggleShowEmoji = () => {
    focusMessageInput();
    data.isEmojiShown = !data.isEmojiShown;
  };

  const sendDefaultMessage = () => {
    sendMessageAndClear(data.messageText, data.attachments);
  };

  const sendMessageAndClear = async (
    text: string,
    attachments: Attachment[],
  ) => {
    if (recordingData.isRecording) {
      await stopRecording();

      // This marks the audio message as in a queue that will be later sent via a side effect connected to recordingData.recordingFile.
      isAudioMessageInQueue.value = true;
      return;
    }

    await handleSendMessage(text, attachments);

    resetInputs();
    focusMessageInput();
  };

  // this sends out the audio message that is in the queue and clears it from the audio input
  watch(
    () => recordingData.recordingFile,
    async (file) => {
      if (!file || !isAudioMessageInQueue.value) {
        return;
      }
      await handleSendMessage(data.messageText, data.attachments);
    },
  );

  const handleSendMessage = async (text: string, attachments: Attachment[]) => {
    const trimmedMessage = getTrimmedText(text);
    const appliedAttachments = await getAppliedAttachments(attachments);
    if (
      (!trimmedMessage && appliedAttachments.length === 0) ||
      (!props.members?.length && chatId.value === ChatTypes.NEW_CHAT_TYPE)
    ) {
      return;
    }

    if (props.newChat) {
      sendNewChatMessage(trimmedMessage, appliedAttachments);
      return;
    }

    sendMessageToChat(chatId.value, trimmedMessage, appliedAttachments);
    isAudioMessageInQueue.value = false;
    clearRecording();
  };

  const focusMessageInput = () => {
    const inputNode = messageInput.value?.$el;
    if (!inputNode) {
      return;
    }
    inputNode.focus({ preventScroll: true });
  };

  const sendNewChatMessage = async (
    messageText: string,
    attachments: Attachment[],
  ) => {
    if (props.alreadyExistsChatId) {
      await sendMessageToChat(
        props.alreadyExistsChatId,
        messageText,
        attachments,
      );
      commit(MutationTypes.SET_SELECTED_CHAT, {
        chatId: props.alreadyExistsChatId,
      });
      router.push(`/${props.alreadyExistsChatId}`);
      return;
    }

    const chatId = await dispatch(ActionTypes.CREATE_CHAT_WITH_MESSAGE, {
      messageText,
      attachments,
      memberIds: memberIds.value,
      members: props.members,
      messageType: messageType.value,
    });

    router.push(`/${chatId}`);
  };

  const sendMessageToChat = async (
    chatId: string,
    messageText: string,
    attachments: Attachment[],
  ) => {
    const replyMess = replyMessage.value;
    const messType = messageType.value;
    const cachedMessage = createCachedMessage(memberIds.value, {
      messageText,
      attachments,
      chatId,
      messageType: messType,
    });
    if (replyMess) {
      const { attachments } = replyMess;
      cachedMessage.replyToMessage = convertNonAutomaticMessageToRawMessage({
        replyMessage: replyMess,
        chatId,
        attachments: attachments || [],
      });
    }
    const modifyMessValue = modifiedMessage.value;
    clearInjectedMessage();

    const sendMessageArgs: ISendMessageArgs = {
      chatId,
      message: cachedMessage,
      messageType: messageType.value,
    };
    if (messType === MessageType.Text && modifyMessValue) {
      sendMessageArgs.modifyingMessageId = modifyMessValue.id;
    }
    try {
      await dispatch(ActionTypes.SEND_MESSAGE, sendMessageArgs);
    } catch (e) {
      console.log(e);
    }
  };

  const resetInputs = () => {
    data.isEmojiShown = false;
    data.messageText = "";
    data.attachments = [];
  };

  const addMessageText = (text: string) => {
    data.messageText += text;
    focusMessageInput();
  };

  const setMessageText = (text: string) => {
    data.messageText = text;
  };

  const addAttachment = async (files: FileList) => {
    data.attachments = [
      ...data.attachments,
      ...(await convertFilesToAttachments(Array.from(files))),
    ];
  };

  const addGifAttachment = async (url: string) => {
    const file = await getFileFromUrl(url, "gif");
    const attachments = await convertFilesToAttachments([file]);
    await handleSendMessage("", attachments);
    focusMessageInput();
    toggleShowEmoji();
  };

  const removeAttachment = (id: string | undefined) => {
    data.attachments = data.attachments.filter((att) => att.id !== id);
  };

  return {
    data,
    isInputShown,
    isMobile,
    toggleShowEmoji,
    addAttachment,
    addMessageText,
    focusMessageInput,
    messageInputHeight,
    removeAttachment,
    setMessageText,
    sendMessage: sendDefaultMessage,
    messageInput,
    addGifAttachment,
    startRecording,
    clearRecording,
    audioPlayerProps,
    isRecordingOrFinished,
    replyMessage,
    modifyMessage: modifiedMessage,
    textMessagePreviewText,
    clearInjectedMessage,
  };
};
